import React, { useMemo } from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useHelpCenterUrl } from "@hooks/useHelpCenterUrl";
import { BalanceSheetImportStep } from "./types";
import { ValidationResult } from "./validation";
import styles from "./Footer.module.scss";

interface Props {
    loading: boolean;
    onSubmit: () => void;
    validationResult: ValidationResult;
    step: BalanceSheetImportStep;
    onNext: () => void;
    onBack: () => void;
    hasAttemptedSubmit: boolean;
}

export const Footer: React.FC<Props> = ({
    loading,
    onSubmit,
    validationResult,
    step,
    onNext,
    onBack,
    hasAttemptedSubmit,
}) => {
    const { errors: validationErrors } = validationResult;
    const helpCenterUrl = useHelpCenterUrl();

    const footerErrorMessage = useMemo(() => {
        if (!hasAttemptedSubmit) {
            return undefined;
        }

        if (validationErrors.balanceSheetDate) {
            return validationErrors.balanceSheetDate;
        }

        if (validationErrors.noAccounts) {
            return validationErrors.noAccounts;
        }

        if (validationErrors.accounts && validationErrors.accounts.length > 0) {
            const accountErrorLines: string[] = [];

            validationErrors.accounts.forEach((account) => {
                if (account.name) {
                    accountErrorLines.push(account.name);
                }
                if (account.accountClass) {
                    accountErrorLines.push(account.accountClass);
                }
                if (account.balance) {
                    accountErrorLines.push(account.balance);
                }
            });

            if (accountErrorLines.length > 1) {
                return "Please complete the highlighted fields";
            }

            if (accountErrorLines.length === 1) {
                return accountErrorLines[0];
            }
        }

        if (validationErrors.balanceSheet) {
            return validationErrors.balanceSheet;
        }

        if (validationErrors.unmappedAccounts) {
            return validationErrors.unmappedAccounts;
        }

        return undefined;
    }, [
        validationErrors.balanceSheet,
        validationErrors.balanceSheetDate,
        validationErrors.noAccounts,
        validationErrors.accounts,
        validationErrors.unmappedAccounts,
        hasAttemptedSubmit,
    ]);

    return (
        <CommandIsland sticky className={styles.commandIsland}>
            {helpCenterUrl && (
                <a
                    href={helpCenterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.helpCenterLink}
                >
                    <span>Questions?</span>{" "}
                    <span className={styles.helpCenterLinkBold}>
                        Open Help Center
                    </span>
                </a>
            )}
            {footerErrorMessage && (
                <p className={styles.footerError}>{footerErrorMessage}</p>
            )}
            <div className={styles.commandIslandSpacer} />
            <Button variant="secondary" size="md" onClick={onBack}>
                Back
            </Button>
            {step === BalanceSheetImportStep.EnterBalances && (
                <WithHotkey
                    hotKey="mod+Enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: "Continue",
                    }}
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onNext}
                            disabled={!!footerErrorMessage}
                            rightIcon={hotkeyHint}
                        >
                            Continue
                        </Button>
                    )}
                </WithHotkey>
            )}
            {step === BalanceSheetImportStep.MapAccounts && (
                <ButtonWithLoader
                    loading={loading}
                    variant="primary"
                    size="md"
                    disabled={!!footerErrorMessage}
                    onClick={onSubmit}
                    type="submit"
                    hotKey="mod+Enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: "Submit",
                    }}
                >
                    Submit
                </ButtonWithLoader>
            )}
        </CommandIsland>
    );
};
